import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { EMPLOYEE_NAV } from "./_nav";
import { api, errFrom } from "../../lib/api";
import { toast } from "sonner";
import { Coffee, MapPin, Clock } from "lucide-react";

export default function EmployeeBreaks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/employee/breaks", { params: { limit: 100 } })
      .then(({ data }) => setItems(data))
      .catch((e) => toast.error(errFrom(e)))
      .finally(() => setLoading(false));
  }, []);

  const totalMin = items.reduce((acc, b) => {
    if (!b.start_at || !b.end_at) return acc;
    return acc + Math.max(0, (new Date(b.end_at) - new Date(b.start_at)) / 60000);
  }, 0);

  return (
    <AppShell nav={EMPLOYEE_NAV} title="Break History" subtitle="All breaks you've taken">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Total breaks" value={items.length} />
        <Stat label="Total minutes" value={`${Math.round(totalMin)}m`} accent />
        <Stat label="Last 30 days" value={items.filter((b) => b.start_at && new Date(b.start_at) > Date.now() - 30 * 86400_000).length} />
        <Stat label="Auto-ended" value={items.filter((b) => b.auto_ended).length} />
      </div>

      <div className="brand-surface rounded-2xl overflow-hidden" data-testid="break-history-table">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[780px]">
          <thead className="bg-[#0A0A0A]">
            <tr className="text-overline text-left">
              <th className="py-3 px-4">Started</th>
              <th className="py-3 px-4">Ended</th>
              <th className="py-3 px-4">Planned</th>
              <th className="py-3 px-4">Actual</th>
              <th className="py-3 px-4">Start location</th>
              <th className="py-3 px-4">End location</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan="6" className="py-10 text-center text-zinc-500">Loading…</td></tr>)}
            {!loading && items.length === 0 && (<tr><td colSpan="6" className="py-12 text-center text-zinc-500">No breaks recorded yet. Take your first break from the Dashboard.</td></tr>)}
            {items.map((b, idx) => {
              const start = new Date(b.start_at);
              const end = b.end_at ? new Date(b.end_at) : null;
              const actualMin = end ? Math.round((end - start) / 60000) : null;
              return (
                <tr key={idx} className="border-t border-white/5" data-testid={`break-row-${idx}`}>
                  <td className="py-3 px-4 text-zinc-300 font-mono text-xs whitespace-nowrap"><Clock size={12} className="inline mr-1 text-[#FF3B30]" />{start.toLocaleString()}</td>
                  <td className="py-3 px-4 text-zinc-300 font-mono text-xs whitespace-nowrap">{end ? end.toLocaleString() : <span className="text-amber-400">active</span>}</td>
                  <td className="py-3 px-4 text-zinc-400"><Coffee size={12} className="inline mr-1 text-amber-400" />{b.planned_minutes}m</td>
                  <td className="py-3 px-4 font-semibold">{actualMin !== null ? `${actualMin}m` : "—"}{b.auto_ended && <span className="ml-2 text-[10px] text-zinc-500">auto-ended</span>}</td>
                  <td className="py-3 px-4 text-zinc-500 max-w-xs truncate"><MapPin size={10} className="inline mr-1" />{b.start_address || (b.start_lat ? `${b.start_lat.toFixed(4)}, ${b.start_lng.toFixed(4)}` : "—")}</td>
                  <td className="py-3 px-4 text-zinc-500 max-w-xs truncate"><MapPin size={10} className="inline mr-1" />{b.end_address || (b.end_lat ? `${b.end_lat.toFixed(4)}, ${b.end_lng.toFixed(4)}` : "—")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`brand-surface rounded-xl p-4 ${accent ? "border-[#FF3B30]/30" : ""}`} data-testid={`break-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-overline">{label}</div>
      <div className={`font-mono font-bold text-3xl mt-2 ${accent ? "text-[#FF3B30]" : "text-white"}`}>{value}</div>
    </div>
  );
}
